import React, { useCallback, useEffect, useMemo, useState } from "react";

import Container from "react-bootstrap/Container";

import { isUndefined } from "lodash";
import { Route, Routes, matchPath, useLocation, useMatch } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { BreadCrumbs } from "./BreadCrumbs";

export interface FormStepProps<TMultiFormData, TFormData> {
  /* the combined data for the entire form, passed to each step component */
  multiFormData: TMultiFormData;
  initialFormData?: TFormData;

  /* function to call to cancel current step */
  onCancel?: () => void;

  /* function to call to submit current step */
  onSubmit: (data: TFormData) => void;
}

type MergeData<TMultiFormData, TFormData> = (multiFormData: TMultiFormData, data: TFormData) => TMultiFormData;

export interface FormStep<TMultiFormData, TFormData, TAdditionalProps = undefined> {
  /* url route to step */
  route: string;

  /* name displayed in form breadcrumb */
  displayName: string;

  /* key used for representing this form's data in the multi form state, overrides mergeData */
  mergeKey?: keyof TMultiFormData;

  /* function which merges the data from this form into the multi form state, if mergeKey isn't present */
  mergeData?: MergeData<TMultiFormData, TFormData>;

  /* called after submit, should return the url route to the next step (or leave undefined if this is the last step) */
  transitionSubmit?: (multiFormData: TMultiFormData) => string;

  /* called after cancel, should return the url route to the previous step (or leave undefined if this is the first step) */
  transitionCancel?: () => string;

  /* the react component containing the form step */
  component: <TProps extends FormStepProps<TMultiFormData, TFormData> & TAdditionalProps>(props: TProps) => JSX.Element;

  additionalComponentProps?: TAdditionalProps;

  shouldMoveToNextStep?: boolean;
}

type MultiStepFormProps<TMultiFormData, TFormData> = {
  baseRoute: string;
  steps: FormStep<TMultiFormData, TFormData, unknown>[];
  initialMultiFormData?: TMultiFormData;
  onCancel?: () => void;
  onSubmit?: (multiFormData: TMultiFormData) => void;
};

export const MultiStepForm = <TMultiFormData, TFormData>({
  baseRoute,
  steps,
  initialMultiFormData,
  onCancel,
  onSubmit,
}: MultiStepFormProps<TMultiFormData, TFormData>) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [multiFormData, setMultiFormData] = useState<TMultiFormData>(initialMultiFormData || ({} as TMultiFormData));
  const hasSteps = steps.length > 0;
  const firstStep = hasSteps ? steps[0] : undefined;
  const matchEmptyPath = useMatch(baseRoute);
  const currentStep = steps.find((step) => matchPath(`${baseRoute}/${step.route}`, pathname));
  const currentStepIndex = currentStep ? steps.indexOf(currentStep) : 0;
  const breadCrumbSteps = steps.map(({ displayName }) => ({
    name: displayName,
  }));
  const navigateRelative = useCallback(
    (route: string, replace: boolean = false) => navigate(`${baseRoute}/${route}`, { replace }),
    [baseRoute, navigate],
  );

  useEffect(() => {
    if (initialMultiFormData) {
      setMultiFormData(initialMultiFormData);
    }
  }, [initialMultiFormData]);

  useEffect(() => {
    if (currentStep?.shouldMoveToNextStep) {
      if (currentStep.transitionSubmit) {
        navigateRelative(currentStep.transitionSubmit(multiFormData));
      }
    }
  }, [multiFormData, currentStep, navigateRelative]);

  useEffect(() => {
    if (matchEmptyPath && firstStep) {
      navigateRelative(firstStep.route, true);
    }
  }, [matchEmptyPath, firstStep, navigateRelative]);

  const onStepSubmit = useCallback(
    (formData: TFormData) => {
      if (currentStep) {
        if (currentStep.mergeKey) {
          setMultiFormData((currentMultiFormData) => ({
            ...currentMultiFormData,
            [currentStep.mergeKey as string]: { ...formData },
          }));
        } else if (currentStep.mergeData) {
          setMultiFormData((currentMultiFormData) =>
            (currentStep.mergeData as MergeData<TMultiFormData, TFormData>)(currentMultiFormData, formData),
          );
        }

        if (currentStep.transitionSubmit) {
          navigateRelative(currentStep.transitionSubmit(multiFormData));
        } else {
          onSubmit && onSubmit(multiFormData);
          setMultiFormData({} as TMultiFormData);
        }
      }
    },
    [currentStep, multiFormData, onSubmit, navigateRelative],
  );

  const onStepCancel = useCallback(() => {
    if (currentStep) {
      if (currentStep.transitionCancel) {
        navigateRelative(currentStep.transitionCancel());
      } else {
        onCancel && onCancel();
      }
    }
  }, [currentStep, onCancel, navigateRelative]);

  const initialFormData = useMemo(() => {
    return isUndefined(currentStep) || isUndefined(currentStep?.mergeKey)
      ? undefined
      : (multiFormData[currentStep.mergeKey as keyof TMultiFormData] as TFormData);
  }, [currentStep, multiFormData]);

  const childProps = {
    multiFormData,
    initialFormData,
    onSubmit: onStepSubmit,
    onCancel: onStepCancel,
    ...(currentStep?.additionalComponentProps ?? {}),
  };

  return (
    <>
      <BreadCrumbs steps={breadCrumbSteps} currentStepIndex={currentStepIndex} />
      <Container>
        {hasSteps && (
          <Routes>
            {steps.map((step) => (
              <Route key={step.route} path={step.route} element={<step.component {...childProps} />}></Route>
            ))}
          </Routes>
        )}
      </Container>
    </>
  );
};
